/**
 * getProxiedImageUrl
 *
 * Proxies image URLs through the image proxy service for optimized delivery.
 * Creates a proxied URL with specified dimensions while preserving aspect ratio.
 *
 * @param url - The original image URL
 * @param width - Desired width (e.g., '600px' or '600'). Defaults to '600px'
 * @param height - Desired height (e.g., 'auto' or '400px'). Defaults to 'auto'
 * @param defaultUrl - Fallback URL to use if the original image fails to load
 * @param fit - Resize fit mode. 'contain' (default) preserves aspect ratio;
 *   'fill' stretches to exactly width×height (used when the user has pinned both
 *   dimensions and wants them honored literally, not aspect-corrected).
 * @param forceTransform - Opt out of the blanket PNG bypass below. Set only by
 *   callers whose fit is a hard requirement rather than a nicety — dynamic image
 *   fields, where the box shape is the whole point. See REVERT MARKER B.
 * @returns Proxied image URL, or original URL if it's a merge tag or placeholder
 */
export function getProxiedImageUrl(
	url: string,
	width?: string,
	height?: string,
	defaultUrl?: string,
	fit: 'contain' | 'fill' | 'cover' = 'contain',
	forceTransform = false,
): string {
	const normalizedUrl = String(url || '').trim();

	const isNonProxyableLocalUrl = (value: string) => {
		if (!value) return true;
		if (
			value.startsWith('data:') ||
			value.startsWith('blob:') ||
			value.startsWith('/') ||
			value.startsWith('./') ||
			value.startsWith('../')
		) {
			return true;
		}

		try {
			const parsedUrl = new URL(value);
			if (!['http:', 'https:'].includes(parsedUrl.protocol)) return true;
			if (
				parsedUrl.hostname === 'localhost' ||
				parsedUrl.hostname === '127.0.0.1' ||
				parsedUrl.hostname === '0.0.0.0'
			) {
				return true;
			}
		} catch {
			// If URL parsing fails, treat it as non-proxyable local-ish input.
			return true;
		}

		return false;
	};

	// Don't proxy if URL is empty, a merge tag, or a placeholder
	if (
		!normalizedUrl ||
		normalizedUrl.includes('{{') ||
		normalizedUrl.includes('placehold.co') ||
		isNonProxyableLocalUrl(normalizedUrl)
	) {
		return normalizedUrl;
	}

	// Preserve PNG transparency by bypassing proxy transformation.
	// Some downstream image pipelines can flatten alpha to black when resizing/transcoding.
	//
	// REVERT MARKER B — the `!forceTransform &&` guard is the only edit here.
	// Dropping it restores the blanket bypass. It exists because logos are
	// almost always transparent PNGs, so the bypass was skipping the resize on
	// exactly the fields whose fit is mandatory. Checked 2026-08-05 against
	// imageproxy.services.bkat.io: an RGBA PNG in came back RGBA, correctly
	// resized — the flattening this guards against no longer reproduces.
	// Untested: palette PNGs carrying tRNS.
	if (!forceTransform && /\.png(?:[?#]|$)/i.test(normalizedUrl)) {
		return normalizedUrl;
	}

	const IMAGE_PROXY_BASE = 'https://imageproxy.services.bkat.io/';

	// Parse width and height, removing 'px' suffix if present
	const w = width?.replace('px', '') || '600';
	const h = height?.replace('px', '') || 'auto';

	// Build proxy URL. fit defaults to 'contain' (preserve aspect ratio).
	const defaultParam = defaultUrl ? `&default=${encodeURIComponent(defaultUrl)}` : '';
	return `${IMAGE_PROXY_BASE}?url=${encodeURIComponent(normalizedUrl)}&w=${w}&h=${h}&fit=${fit}${defaultParam}`;
}
